import { Core, Decorative } from "@faire/design-tokens";
import { TypographyStyles } from "@faire/web/slate/Typography";
import React from "react";
import styled, { css } from "styled-components";

import { INavigationLink } from "@faire/retailer/components/Header/desktop/NavigationLinksBar/__internal__/INavigationLink";
import { NavigationLink } from "@faire/retailer/components/Header/desktop/NavigationLinksBar/__internal__/NavigationLink";
import { trackRetailerEvent } from "@faire/retailer/lib/trackRetailerEvent";

interface IProps {
  link: INavigationLink;
  selectedLinkName: string | undefined;
  onClick: (link: INavigationLink) => void;
  onMouseEnter: (link: INavigationLink) => void;
  className?: string;
}

export const L1Link: React.FC<IProps> = ({
  link,
  selectedLinkName,
  onClick,
  onMouseEnter,
  className,
}) => {
  const handleClick = React.useCallback(() => {
    onClick(link);
    trackRetailerEvent(`/header-categories/${link.name}`);
  }, [link, onClick]);
  const handleMouseEnter = React.useCallback(() => {
    onMouseEnter(link);
  }, [link, onMouseEnter]);
  const linkColorOverride = selectedLinkName ? Core.text.subdued : undefined;

  const isSalesLink = link.categoryPathIdentifier === "sale";

  return (
    <Wrapper
      aria-haspopup="menu"
      onMouseEnter={handleMouseEnter}
      onFocus={handleMouseEnter}
      $isSelected={link.name === selectedLinkName}
      className={className}
      $linkColorOverride={
        isSalesLink ? Decorative.shade.orange : linkColorOverride
      }
      $hasLink={!!link.to}
    >
      <StyledLink
        link={link}
        onClick={handleClick}
        dataTestId={`header-${
          link.categoryPathIdentifier ?? "category"
        }-dropdown-link`}
      >
        {link?.name}
      </StyledLink>
    </Wrapper>
  );
};

const StyledLink = styled(NavigationLink)`
  ${TypographyStyles({ as: undefined })};
  border-bottom: 1px solid transparent;
  white-space: nowrap;

  &:active,
  &:focus {
    color: ${Core.text.subdued};
  }
`;

const Wrapper = styled.div<{
  $isSelected: boolean;
  $linkColorOverride: string | undefined;
  $hasLink: boolean;
}>`
  display: flex;
  cursor: ${(props) => (props.$hasLink ? "pointer" : "default")};

  ${(props) =>
    props.$linkColorOverride
      ? css`
          ${StyledLink} {
            color: ${props.$linkColorOverride};
          }
        `
      : ""};

  ${(props) =>
    props.$isSelected
      ? css`
          ${StyledLink} {
            color: ${Core.text.default};
            border-bottom-color: ${Core.text.default};
          }
        `
      : ""};
`;
